import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { setSnackBarNotification } from 'state/ProjectTasksList/actions/actionsSlice';
import { bulkDeleteTasks } from 'api/tasksList';
import {
  getBulkDeleteTasksUrl,
  deserializeBulkDeleteTasksResponse,
} from 'state/ProjectTasksList/bulkActions/bulkDelete/utils';

export interface UseDeleteTaskParams {
  taskId: string;
  projectId: string;
  showNotification: (params: any) => void;
  onSuccessCallback?: () => void;
}

interface UseDeleteTaskReturn {
  deleteTask: () => void;
}

const useDeleteTask: (params: UseDeleteTaskParams) => UseDeleteTaskReturn = ({
  taskId,
  projectId,
  showNotification,
  onSuccessCallback = () => {},
}) => {
  const dispatch = useDispatch();

  const deleteTask = useCallback(async () => {
    try {
      const url = getBulkDeleteTasksUrl({
        projectId,
        exportState: {
          selectAll: false,
          excludedIds: [],
          selectedIds: [taskId],
        },
      });
      const response = await bulkDeleteTasks(url, {
        headers: {
          'enable-email-notification': 'true',
        },
      });
      const deserializedResponse = await deserializeBulkDeleteTasksResponse(
        response
      );

      if (deserializedResponse.failedCount > 0) {
        throw new Error('Failed to delete task');
      }

      dispatch(
        setSnackBarNotification({
          notificationMessage: intl.get(
            'TASKS.TASK_DETAIL_PAGE.DELETE_SUCCESS'
          ),
        })
      );
      onSuccessCallback();
    } catch (error) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get(
          'TASKS.TASK_DETAIL_PAGE.DELETE_ERROR.TITLE'
        ),
        notificationMessage: intl.get(
          'TASKS.TASK_DETAIL_PAGE.DELETE_ERROR.MESSAGE'
        ),
      });
    }
  }, [dispatch, showNotification, taskId, projectId, onSuccessCallback]);

  return useMemo(() => ({ deleteTask }), [deleteTask]);
};

export default useDeleteTask;
